import React from "react";
import {action, observable} from "mobx";
import {observer} from "mobx-react";
import {withToolbar} from "sulu-admin-bundle/containers";
import {translate} from "sulu-admin-bundle/utils";
import {Requester} from "sulu-admin-bundle/services";
import Input from "sulu-admin-bundle/components/Input";
import Loader from "sulu-admin-bundle/components/Loader";

const ENDPOINT = "/admin/api/privacy-policy";

@observer
class PrivacyPolicySettingsView extends React.Component {
    @observable loading = true;
    @observable saving = false;
    @observable version = "";
    @observable url = null;
    @observable errorMessage = null;
    @observable successMessage = null;

    componentDidMount() {
        this.load();
    }

    @action load = () => {
        this.loading = true;
        Requester.get(ENDPOINT)
            .then(
                action((data) => {
                    this.version = data.version || "";
                    this.url = data.url || null;
                    this.loading = false;
                })
            )
            .catch(
                action(() => {
                    this.errorMessage = translate("privacy.save_error");
                    this.loading = false;
                })
            );
    };

    @action handleVersionChange = (value) => {
        this.version = value ?? "";
        this.successMessage = null;
    };

    @action handleUrlChange = (value) => {
        this.url = value ?? null;
        if (this.url === "") {
            this.url = null;
        }
        this.successMessage = null;
    };

    @action save = () => {
        if (!this.canSave()) {
            return Promise.resolve();
        }

        this.saving = true;
        this.errorMessage = null;
        this.successMessage = null;

        return Requester.post(ENDPOINT, {
            version: this.version.trim(),
            url: this.url,
        })
            .then(
                action((data) => {
                    this.version = data.version || "";
                    this.url = data.url || null;
                    this.saving = false;
                    this.successMessage = translate("privacy.save_success");
                })
            )
            .catch((error) => {
                if (error && typeof error.json === "function") {
                    return error.json().then(
                        action((body) => {
                            this.errorMessage = (body && body.message) || translate("privacy.save_error");
                            this.saving = false;
                        })
                    );
                }
                action(() => {
                    this.errorMessage = translate("privacy.save_error");
                    this.saving = false;
                })();
            });
    };

    canSave() {
        return !this.saving && this.version.trim() !== "";
    }

    render() {
        if (this.loading) {
            return (
                <div style={{padding: 40, display: "flex", justifyContent: "center"}}>
                    <Loader />
                </div>
            );
        }

        const canEdit = !!(this.props.route && this.props.route.options && this.props.route.options.canEdit);

        return (
            <div style={{padding: 32, maxWidth: 720}}>
                <h1 style={{marginTop: 0}}>{translate("privacy.menu")}</h1>

                {this.successMessage && (
                    <div
                        style={{
                            background: "#e8f5e9",
                            border: "1px solid #a5d6a7",
                            borderRadius: 4,
                            padding: "10px 12px",
                            marginBottom: 16,
                        }}
                    >
                        {this.successMessage}
                    </div>
                )}

                {this.errorMessage && (
                    <div
                        style={{
                            background: "#ffebee",
                            border: "1px solid #ef9a9a",
                            borderRadius: 4,
                            padding: "10px 12px",
                            marginBottom: 16,
                        }}
                    >
                        {this.errorMessage}
                    </div>
                )}

                <div style={{marginBottom: 20, maxWidth: 320}}>
                    <label style={{display: "block", marginBottom: 6, fontWeight: 600}}>
                        {translate("privacy.version")} *
                    </label>
                    <Input
                        disabled={!canEdit}
                        onChange={this.handleVersionChange}
                        value={this.version}
                    />
                </div>

                <div style={{marginBottom: 20}}>
                    <label style={{display: "block", marginBottom: 6, fontWeight: 600}}>
                        {translate("privacy.url")}
                    </label>
                    <Input
                        disabled={!canEdit}
                        onChange={this.handleUrlChange}
                        value={this.url || ""}
                    />
                    <small style={{color: "#666"}}>
                        {translate("privacy.url_help")}
                    </small>
                </div>
            </div>
        );
    }
}

export default withToolbar(PrivacyPolicySettingsView, function () {
    return {
        items: [
            {
                type: "button",
                label: translate("sulu_admin.save"),
                icon: "su-save",
                disabled: !this.canSave(),
                loading: this.saving,
                onClick: this.save,
            },
        ],
    };
});
